import { Procedure } from "../types/procedures";

/**
 * Minimal shape needed to derive a patient-facing procedure title. The full
 * `Procedure` type satisfies it; callers may also pass any object carrying
 * `name` + (optional) `marketingName` / `marketingNameEs`.
 */
type ProcedureNameFields = Pick<Procedure, "name"> & {
  marketingName?: string | null;
  marketingNameEs?: string | null;
};

/** A `t` function compatible with how react-i18next is called in the funnel. */
type TranslateFn = (key: string, opts?: { defaultValue?: string }) => string;

/**
 * Patient-facing procedure title shown across the quote funnel (signature
 * cards, catalog cards, info modal, quote summary, surgeon step, campaign
 * heading). Prefers the admin-set marketing name — the same field family the
 * booking calendar uses — over the internal/clinical `name`.
 *
 * Localization: for any Spanish locale (matched with `.includes("es")` so
 * region variants like "es-US"/"es-419" count — the funnel's LanguageSwitcher
 * sets "es-US") the Spanish marketing name (`marketingNameEs`) is preferred and
 * the English `marketingName` is never shown; otherwise `marketingName` is
 * used. Marketing names are free-text editorial copy (no i18n key), so they're
 * rendered as-is.
 *
 * When no marketing name applies, the fallback preserves each surface's i18n:
 * pass `t` (a `procedures` namespace `t`) to use the locale-aware
 * `procedures.*` translation — so a Spanish patient still sees the curated
 * Spanish title even before any `marketingNameEs` is set — or omit it to fall
 * back to the raw `name`.
 */
export const procedureDisplayName = (
  procedure: ProcedureNameFields,
  opts: { language?: string; t?: TranslateFn } = {},
): string => {
  const { language, t } = opts;
  // Match any Spanish locale, incl. region variants ("es-US", "es-419"). This
  // mirrors the codebase's canonical check (QuoteProvider's i18n.language
  // .includes("es")) — the funnel's LanguageSwitcher sets "es-US", not "es".
  const isSpanish = language?.includes("es") ?? false;
  const marketing = isSpanish
    ? procedure.marketingNameEs?.trim()
    : procedure.marketingName?.trim();
  if (marketing) return marketing;
  return t
    ? t(`procedures.${procedure.name}`, { defaultValue: procedure.name })
    : procedure.name;
};
